/**
 * assets/prohats-collection.js
 * 
 * AJAX-based filter and catalog management for Prohats collection pages (PLP).
 * Integrates native Shopify Storefront filters with mobile drawer panels,
 * details accordion persistence, and smooth AJAX DOM swapping.
 */

document.addEventListener('DOMContentLoaded', () => {

  const collectionSection = document.querySelector('.collection-main-section');
  if (!collectionSection) return;

  const sectionId = collectionSection.getAttribute('data-id');
  const filtersForm = document.getElementById('CollectionFiltersForm');
  const productsContainer = document.getElementById('ProductGridContainer');

  const SELECTORS = {
    productsGrid: '[data-products-grid]',
    pagination: '[data-pagination-container]',
    activeFilters: '.active-filters-row',
    sidebarForm: '#CollectionFiltersForm',
    toolbarSort: '[data-sort-select]',
    toolbarCrown: '[data-crown-select]',
    toolbar: '.collection-toolbar'
  };

  let currentView = window.innerWidth >= 768 ? '3' : '1';

  applyViewLayout(currentView);
  initFilters();
  initFilterToggle();
  initViewSelectors();
  initMobileDrawer();
  initHeroCarousel();

  /**
   * Initialize athlete hero multi-image carousel
   */
  function initHeroCarousel() {
    const carousels = document.querySelectorAll('.athlete-hero-image-wrap.has-carousel');
    carousels.forEach(carousel => {
      const slides = carousel.querySelectorAll('.athlete-hero-slide');
      const total = parseInt(carousel.getAttribute('data-total-slides') || slides.length, 10);
      const intervalSec = parseInt(carousel.getAttribute('data-autoplay') || 5, 10);
      if (total <= 1) return;

      let currentSlide = 0;
      let timer = null;

      const heroContainer = carousel.closest('.collection-athlete-hero');
      const textSlides = heroContainer ? heroContainer.querySelectorAll('.athlete-hero-text-slide') : [];

      function goToSlide(index) {
        currentSlide = (index + total) % total;
        slides.forEach((slide, i) => {
          if (i === currentSlide) {
            slide.classList.add('is-active');
          } else {
            slide.classList.remove('is-active');
          }
        });

        const activeSlideIndex = slides[currentSlide] ? slides[currentSlide].getAttribute('data-slide-index') : currentSlide.toString();
        textSlides.forEach(textSlide => {
          if (textSlide.getAttribute('data-slide-index') === activeSlideIndex) {
            textSlide.classList.add('is-active');
          } else {
            textSlide.classList.remove('is-active');
          }
        });
      }

      function startAutoplay() {
        stopAutoplay();
        timer = setInterval(() => {
          goToSlide(currentSlide + 1);
        }, intervalSec * 1000);
      }

      function stopAutoplay() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      startAutoplay();
    });
  }
  function initFilters() {
    if (!filtersForm) return;

    // Listen for form inputs (checkboxes, inputs) changing
    filtersForm.addEventListener('input', debounce((e) => {
      if (e.target.type === 'number') return;
      onFilterChange();
    }, 500));

    // Handle number inputs (min/max price) on blur or enter key
    filtersForm.addEventListener('change', (e) => {
      if (e.target.type === 'number') {
        onFilterChange();
      }
    });

    // Handle dropdown select changes (Sort By and Crown)
    bindToolbarSelects();

    // Intercept pagination and remove filter pill clicks to load via AJAX
    document.addEventListener('click', (e) => {
      const pageLink = e.target.closest('[data-page-link]');
      const removeLink = e.target.closest('[data-remove-filter]');
      const clearAllLink = e.target.closest('[data-clear-filters]');

      if (pageLink || removeLink || clearAllLink) {
        e.preventDefault();
        const targetUrl = (pageLink || removeLink || clearAllLink).getAttribute('href');
        if (targetUrl) {
          fetchFilteredData(targetUrl);
        }
      }
    });

    // Intercept browser back/forward buttons
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.path) {
        fetchFilteredData(e.state.path, false);
      }
    });
  }

  /**
   * Action handler when a filter changes in the form
   */
  function onFilterChange() {
    const formData = new FormData(filtersForm);
    const searchParams = new URLSearchParams(formData);
    
    // Explicitly add sort value from toolbar select if present
    const sortSelect = document.querySelector(SELECTORS.toolbarSort);
    if (sortSelect) {
      searchParams.set('sort_by', sortSelect.value);
    }

    // Explicitly add crown value from toolbar select if present
    const crownSelect = document.querySelector(SELECTORS.toolbarCrown);
    if (crownSelect && crownSelect.value) {
      searchParams.set(crownSelect.name, crownSelect.value);
    } else if (crownSelect) {
      searchParams.delete(crownSelect.name);
    }

    // Always reset pagination page to 1 when filters change
    searchParams.delete('page');

    // Generate destination URL
    const baseUrl = window.location.pathname;
    const targetUrl = `${baseUrl}?${searchParams.toString()}`;
    
    fetchFilteredData(targetUrl);
  }

  /**
   * Fetches section rendering payload from Shopify and performs DOM swap
   */
  function fetchFilteredData(url, updateHistoryState = true) {
    // Store accordion states before swap
    const accordionStates = {};
    document.querySelectorAll('details.filter-group-wrapper').forEach(details => {
      const index = details.getAttribute('data-index');
      accordionStates[index] = details.hasAttribute('open');
    });

    // Add loading states
    productsContainer.classList.add('loading');
    
    // Construct URL with Section Rendering API parameter
    const cleanUrl = url.includes('?') ? url : `${url}?`;
    const renderUrl = `${cleanUrl}&section_id=${sectionId}`;

    fetch(renderUrl)
      .then(response => response.text())
      .then(htmlText => {
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(htmlText, 'text/html');

        // Swap target panels
        swapDOMElement('#ProductGridContainer', parsedHtml);
        swapDOMElement(SELECTORS.activeFilters, parsedHtml);
        swapDOMElement(SELECTORS.sidebarForm, parsedHtml);
        swapDOMElement(SELECTORS.toolbar, parsedHtml);

        // Restore accordion states immediately after swap
        document.querySelectorAll('details.filter-group-wrapper').forEach(details => {
          const index = details.getAttribute('data-index');
          if (accordionStates[index] !== undefined) {
            if (accordionStates[index]) {
              details.setAttribute('open', '');
            } else {
              details.removeAttribute('open');
            }
          }
        });

        // Keep local view selection intact after swap
        applyViewLayout(currentView);

        // Keep filter trigger state in sync after swap
        const layoutGrid = document.getElementById('CollectionLayoutGrid');
        if (layoutGrid) {
          applyFiltersOpenState(layoutGrid.classList.contains('filters-open'));
        }

        // Remove loading state
        productsContainer.classList.remove('loading');

        // Update history url
        if (updateHistoryState) {
          history.pushState({ path: url }, '', url);
        }

        // Re-bind listeners that might have been destroyed in the DOM swap
        bindToolbarSelects();
        initMobileDrawer();
        initHeroCarousel();
      })
      .catch(err => {
        console.error("AJAX collection filtration fetch failed:", err);
        productsContainer.classList.remove('loading');
      });
  }

  function bindToolbarSelects() {
    const sortSelect = document.querySelector(SELECTORS.toolbarSort);
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        onFilterChange();
      });
    }

    const crownSelect = document.querySelector(SELECTORS.toolbarCrown);
    if (crownSelect) {
      crownSelect.addEventListener('change', () => {
        onFilterChange();
      });
    }

    const customCrownSelect = document.querySelector('[data-crown-select-custom]');
    if (customCrownSelect) {
      customCrownSelect.addEventListener('change', () => {
        const val = customCrownSelect.value;
        const checkboxes = document.querySelectorAll('.hidden-crown-filter-group input[type="checkbox"]');
        checkboxes.forEach(cb => {
          const cbVal = cb.value.toLowerCase();
          if (val === 'precreased') {
            cb.checked = cbVal.includes('plega') || cbVal.includes('precorte') || cbVal.includes('precrease');
          } else if (val === 'open') {
            cb.checked = cbVal.includes('open') || cbVal.includes('abier') || cbVal.includes('abrir');
          } else {
            cb.checked = false;
          }
        });
        onFilterChange();
      });
    }
  }

  function swapDOMElement(selector, parsedHtml) {
    const currentEl = document.querySelector(selector);
    const newEl = parsedHtml.querySelector(selector);

    if (currentEl && newEl) {
      currentEl.innerHTML = newEl.innerHTML;
    } else if (currentEl && !newEl) {
      currentEl.innerHTML = '';
    }
  }

  function initViewSelectors() {
    document.addEventListener('click', (e) => {
      const viewBtn = e.target.closest('[data-view-val]');
      if (!viewBtn) return;

      const viewVal = viewBtn.getAttribute('data-view-val');
      
      const siblings = viewBtn.parentElement.querySelectorAll('[data-view-val]');
      siblings.forEach(s => s.classList.remove('active'));
      viewBtn.classList.add('active');

      currentView = viewVal;
      applyViewLayout(viewVal);
    });
  }

  function applyViewLayout(viewVal) {
    const grid = document.querySelector(SELECTORS.productsGrid);
    if (!grid) return;

    grid.className = 'collection-products-grid';
    grid.classList.add(`view-${viewVal}`);

    const buttons = document.querySelectorAll(`[data-view-val]`);
    buttons.forEach(btn => {
      if (btn.getAttribute('data-view-val') === viewVal) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  function initFilterToggle() {
    const layoutGrid = document.getElementById('CollectionLayoutGrid');
    const mainSection = document.querySelector('.collection-main-section');
    if (!layoutGrid && !mainSection) return;

    // Desktop saved state check (default: collapsed / false)
    const isDesktop = window.innerWidth >= 1024;
    const savedState = sessionStorage.getItem('prohats_filters_open');
    const shouldBeOpen = savedState === 'true' && isDesktop;

    applyFiltersOpenState(shouldBeOpen);

    // Global listener for filter triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-filter-toggle]');
      if (!trigger) return;

      e.preventDefault();
      const currentIsDesktop = window.innerWidth >= 1024;
      const targetGrid = document.getElementById('CollectionLayoutGrid');
      const targetSection = document.querySelector('.collection-main-section');
      const sidebar = document.getElementById('CollectionSidebar');
      const backdrop = document.getElementById('FiltersBackdrop');

      if (currentIsDesktop) {
        const currentlyOpen = targetGrid ? targetGrid.classList.contains('filters-open') : false;
        const nextState = !currentlyOpen;
        applyFiltersOpenState(nextState);
        sessionStorage.setItem('prohats_filters_open', nextState ? 'true' : 'false');
      } else {
        // Mobile drawer open
        if (sidebar && backdrop) {
          sidebar.classList.add('active');
          backdrop.classList.add('active');
          document.body.style.overflow = 'hidden';
        }
      }
    });
  }

  function applyFiltersOpenState(isOpen) {
    const layoutGrid = document.getElementById('CollectionLayoutGrid');
    const mainSection = document.querySelector('.collection-main-section');

    if (layoutGrid) {
      if (isOpen) {
        layoutGrid.classList.add('filters-open');
      } else {
        layoutGrid.classList.remove('filters-open');
      }
    }

    if (mainSection) {
      if (isOpen) {
        mainSection.classList.add('filters-open');
      } else {
        mainSection.classList.remove('filters-open');
      }
    }

    document.querySelectorAll('[data-filter-toggle]').forEach(btn => {
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function initMobileDrawer() {
    const openBtn = document.querySelector('[data-mobile-filters-open]');
    const closeBtns = document.querySelectorAll('[data-mobile-filters-close]');
    const sidebar = document.getElementById('CollectionSidebar');
    const backdrop = document.getElementById('FiltersBackdrop');

    if (openBtn && sidebar && backdrop) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        sidebar.classList.add('active');
        backdrop.classList.add('active');
        document.body.style.overflow = 'hidden';
      });
    }

    closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (sidebar) sidebar.classList.remove('active');
        if (backdrop) backdrop.classList.remove('active');
        document.body.style.overflow = '';
      });
    });
  }

  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
});
